import { useEffect, useState, type CSSProperties, type RefObject } from 'react'
import { createPortal } from 'react-dom'

import AgentV2MatterArtifactEditLoadingReview from '@/agent-v2/artifact-edit-review/AgentV2MatterArtifactEditLoadingReview'
import AgentV2PendingMatterArtifactEdit from '@/agent-v2/artifact-edit-review/AgentV2PendingMatterArtifactEdit'
import type { AgentV2MatterArtifactEditProposalReviewContext } from '@/agent-v2/artifact-edit-review/store/selectors'

/** Minimum usable width for a review shown within the docked chat panel. */
const kMinimumReviewWidth = 448
/** Keep the same separation around the review in side-by-side and overlay layouts. */
const kReviewSpacing = 16
/** Gap kept between the review and the viewport edges. */
const kViewportMargin = 8
/** Room kept above the panel so a long edit doesn't reach the header. */
const kTopInset = 16
/** Room kept below the panel: enough to clear the reopen button, reserved always so the height doesn't change when the chat toggles. */
const kBottomInset = 80
/** Off-screen until the panel has been measured for the current proposal. */
const kHiddenStyle: CSSProperties = { position: 'fixed', zIndex: 20, opacity: 0 }

// eslint-disable-next-line react-refresh/only-export-components -- Consumers share this component's discriminated state contract.
export enum PendingEditKind {
  LOADING = 'loading',
  REVIEW = 'review',
}

export type PendingEditState =
  | {
      kind: PendingEditKind.LOADING
      canCancel: boolean
      onCancel: () => void
    }
  | {
      kind: PendingEditKind.REVIEW
      context: AgentV2MatterArtifactEditProposalReviewContext
    }

type FloatingPendingEditReviewProps = {
  /** The document element the review is positioned against. */
  draftRef: RefObject<HTMLElement | null>
  /** The docked chat panel the review should sit within when there is enough room. */
  chatPanelRef?: RefObject<HTMLElement | null>
  conversationId: string
  pendingEdit: PendingEditState
}

/** Floats the loading or completed inline-edit review beside the document in the drafting workspace. */
export default function FloatingPendingEditReview({ draftRef, chatPanelRef, conversationId, pendingEdit }: FloatingPendingEditReviewProps) {
  const [style, setStyle] = useState<CSSProperties>(kHiddenStyle)
  const [positionedReviewId, setPositionedReviewId] = useState<string | null>(null)
  // Review context is a fresh object each render, so key positioning on a stable id.
  const reviewId = pendingEdit.kind === PendingEditKind.REVIEW ? pendingEdit.context.proposal.proposalId : `loading:${conversationId}`

  useEffect(() => {
    const draft = draftRef.current
    if (!draft) return

    let frame: number | null = null
    const update = () => {
      frame = null
      const rect = draft.getBoundingClientRect()
      // Responsive draft-only layouts keep page chrome around the actual document. Anchor overlay
      // spacing to that visible surface so its existing margin does not consume the review gap.
      const documentRect = draft.querySelector<HTMLElement>('.prose-editor-container')?.getBoundingClientRect() ?? rect
      const chatPanelRect = chatPanelRef?.current?.getBoundingClientRect()
      const maximumWidth = Math.max(0, window.innerWidth - 2 * kViewportMargin)
      const chatPanelWidth = chatPanelRect ? Math.max(0, chatPanelRect.width - 2 * kReviewSpacing) : 0
      const reviewFitsChatPanel = chatPanelRect !== undefined && chatPanelWidth >= kMinimumReviewWidth
      const overlayWidth = Math.max(0, documentRect.width - 2 * kReviewSpacing)
      const width = reviewFitsChatPanel ? Math.min(chatPanelWidth, maximumWidth) : Math.min(overlayWidth, maximumWidth)
      const centeredOverlayLeft = documentRect.left + (documentRect.width - width) / 2
      const left = reviewFitsChatPanel
        ? chatPanelRect.left + kReviewSpacing
        : Math.max(kViewportMargin, Math.min(centeredOverlayLeft, window.innerWidth - kViewportMargin - width))
      setStyle({
        position: 'fixed',
        top: rect.top + kTopInset,
        left,
        height: Math.max(0, rect.height - kTopInset - kBottomInset),
        width,
        zIndex: 20,
        opacity: 1,
      })
      setPositionedReviewId(reviewId)
    }
    const schedule = () => {
      if (frame !== null) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(update)
    }

    schedule()
    const resizeObserver = new ResizeObserver(schedule)
    resizeObserver.observe(draft)
    if (chatPanelRef?.current) resizeObserver.observe(chatPanelRef.current)
    window.addEventListener('resize', schedule)
    window.addEventListener('transitionend', schedule, true)
    return () => {
      if (frame !== null) cancelAnimationFrame(frame)
      resizeObserver.disconnect()
      window.removeEventListener('resize', schedule)
      window.removeEventListener('transitionend', schedule, true)
    }
  }, [reviewId, draftRef, chatPanelRef])

  // Portalled to the body so its fixed positioning can't be captured by a transformed ancestor.
  return createPortal(
    <div className="pointer-events-none" style={positionedReviewId === reviewId ? style : kHiddenStyle}>
      {pendingEdit.kind === PendingEditKind.REVIEW ? (
        <AgentV2PendingMatterArtifactEdit context={pendingEdit.context} conversationId={conversationId} />
      ) : (
        <div className="flex h-full flex-col justify-center">
          <AgentV2MatterArtifactEditLoadingReview canCancel={pendingEdit.canCancel} onCancel={pendingEdit.onCancel} />
        </div>
      )}
    </div>,
    document.body
  )
}
